"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";

const SCRIPT_SRC_BASE = "https://app.termly.io";

interface TermlyCMPProps {
	/**
	 * Enables Termly Autoconsent blocking. Leave undefined / false to disable.
	 */
	autoBlock?: boolean;
	/**
	 * Optional parent-domain origin to enable cross-subdomain consent sharing.
	 * Example: "https://example.com"
	 */
	masterConsentsOrigin?: string;
	/**
	 * Your Termly website UUID.
	 */
	websiteUUID: string;
}

export default function TermlyCMP({
	autoBlock,
	masterConsentsOrigin,
	websiteUUID,
}: TermlyCMPProps) {
	const scriptSrc = useMemo(() => {
		const src = new URL(SCRIPT_SRC_BASE);
		src.pathname = `/resource-blocker/${websiteUUID}`;
		if (autoBlock) {
			src.searchParams.set("autoBlock", "on");
		}
		if (masterConsentsOrigin) {
			src.searchParams.set("masterConsentsOrigin", masterConsentsOrigin);
		}
		return src.toString();
	}, [autoBlock, masterConsentsOrigin, websiteUUID]);

	useEffect(() => {
		const script = document.createElement("script");
		script.src = scriptSrc;
		script.type = "text/javascript";
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	}, []);

	const pathname = usePathname();
	const searchParams = useSearchParams();

	useEffect(() => {
		window.Termly?.initialize?.();
	}, [pathname, searchParams]);

	return null;
}
